import { Form, Input, Layout, message, Spin } from "antd"
import axios from "axios"
import { Link } from "gatsby"
import React, { useEffect, useState } from "react"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { solutions } from "../data/solutions"
import "../styles/footer.scss"

// Five sitewide links to the offer pages, replacing the single hub link
// (§4.2). Resolved from solutions.js by id rather than hard-coded paths, so a
// slug can never drift out of sync with the route that actually builds.
const FOOTER_OFFER_IDS = [
  "fit-sprint",
  "ai-voice-receptionist",
  "installer-platform",
  "odoo-manufacturers",
  "practice-telehealth",
]

const footerOffers = FOOTER_OFFER_IDS.map(id =>
  solutions.find(s => s.id === id)
).filter(Boolean)

const IconLinkedIn = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect x="2" y="9" width="4" height="12" />
    <circle cx="4" cy="4" r="2" />
  </svg>
)

const IconFacebook = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
)

const IconInstagram = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
)

const IconYouTube = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.97C18.88 4 12 4 12 4s-6.88 0-8.59.45a2.78 2.78 0 0 0-1.95 1.97A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.4a2.78 2.78 0 0 0 1.95-1.97A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
    <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" />
  </svg>
)

const Footer = () => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [isVerified, setIsVerified] = useState(false)

  const { Footer: AntFooter } = Layout

  useEffect(() => {
    const getToken = async () => {
      if (!executeRecaptcha) return
      const token = await executeRecaptcha("newsletter_subscribe")
      const res = await fetch("https://staging.visiomate.com/verify-recaptcha-site", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      })
      const data = await res.json()
      setIsVerified(data.success && data.score > 0.5)
    }
    getToken()
  }, [executeRecaptcha])

  const onFinish = values => {
    sendFormData({ name: values.name, email: values.email })
    form.resetFields()
  }

  const sendFormData = async data => {
    setLoading(true)
    try {
      await axios.post("https://staging.visiomate.com/newsletter", data)
      setLoading(false)
      message.success("Subscribed successfully!")
    } catch (error) {
      setLoading(false)
      message.error("Failed to subscribe. Please try again.")
    }
  }

  return (
    <AntFooter className="vf">
      {/* ── Main grid: brand + 6 nav columns ── */}
      <div className="vf__main">
        <div className="vf__brand">
          <p className="vf__tagline">
            Partnering with visionary businesses since 2018 — we build and grow
            software that puts customers first.
          </p>
          <div className="vf__quick">
            <Link to="/about">About Us</Link>
            <Link to="/lifeAtVisiomate">Life at Visiomate</Link>
            <Link to="/career">Careers</Link>
            <Link to="/NEWS">News</Link>
            <Link to="/WhitepapereBooks">Downloads</Link>
          </div>
        </div>

        <div className="vf__col">
          <span className="vf__col-label">Services</span>
          <Link to="/services/webdevelopment">Tailor-made Web Development</Link>
          <Link to="/services/mobiledevelopment">Tailor-made Mobile App Development</Link>
          <Link to="/services/staffaugmentation">Staff Augmentation</Link>
          <Link to="/services/aisolution">AI-Native Development</Link>
        </div>

        <div className="vf__col">
          {/* "Offers", matching the route. The second column further down is
              also headed "Solutions" and lists product case studies — two
              columns with the same label was already confusing, and only this
              one is the offer catalogue. */}
          {/* The heading is a LINK, not a label. The five rows below it go to
              individual offer pages; /offers itself — the catalogue of
              everything we sell — had no sitewide chrome link at all until now
              (NAVIGATION-AUDIT §2). This is the footer half of that fix; the
              header carries the other. */}
          <Link to="/offers" className="vf__col-label vf__col-label--link">
            Offers
          </Link>
          {footerOffers.map(s => (
            <Link key={s.slug} to={`/offers/${s.slug}`}>
              {s.name}
            </Link>
          ))}
        </div>

        <div className="vf__col">
          {/* Was five bare <span> rows with no route at all — the bug this
              fixes (src/docs/visiomate-industries-rebuild-brief.md). The
              heading stays a label rather than a link, matching Services/ERP/
              Solutions/Case Studies below: none of those has a hub page
              either, and only Offers does (it links because /offers exists).
              There is no /industries/ hub page (out of scope for this fix),
              so linking the heading would point at a page that doesn't exist.
              Education and Construction & Engineering are held back same as
              the header — no page built yet, so no link yet. */}
          <span className="vf__col-label">Industries</span>
          <Link to="/industries/manufacturing">Manufacturing</Link>
          <Link to="/industries/retail">Retail</Link>
          <Link to="/industries/distribution">Distribution</Link>
          <Link to="/industries/healthcare">Healthcare</Link>
          <Link to="/industries/events-production">Events & Production</Link>
          <Link to="/industries/field-service">Field Service</Link>
          <Link to="/industries/real-estate">Real Estate</Link>
        </div>

        <div className="vf__col">
          <span className="vf__col-label">ERP</span>
          <Link to="/dynamics365/financeOperations">Microsoft Dynamics 365 F&O - Support</Link>
          <Link to="/Odoo/implementationPartner">Odoo Implementation - Certified Partner</Link>
        </div>

        <div className="vf__col">
          <span className="vf__col-label">Solutions</span>
          <Link to="/caseStudies/products/donationCampaignManagement">Donation Management</Link>
          <Link to="/caseStudies/products/visioedge">Visioedge ERP</Link>
          <Link to="/caseStudies/products/viztos">Viztos POS</Link>
          <Link to="/caseStudies/products/mD365BulkStockDistribution">D365 Bulk Stock</Link>
          <Link to="/caseStudies/products/classManagement">Class Management</Link>
        </div>

        <div className="vf__col">
          {/* Used to list five individual stories by hand — /caseStudies now
              lists every story and product in one place, so the heading
              links there and the list is just that one link. */}
          <Link to="/caseStudies" className="vf__col-label vf__col-label--link">
            Case Studies
          </Link>
          <Link to="/caseStudies">View all case studies</Link>
        </div>
      </div>

      {/* ── Bottom bar: social | newsletter | copyright ── */}
      <div className="vf__bottom">
        <div className="vf__social">
          <a href="https://www.linkedin.com/company/visiomate" target="_blank" rel="noreferrer" className="vf__social-btn" aria-label="LinkedIn">
            <IconLinkedIn />
          </a>
          <a href="https://www.facebook.com/visiomateofficial" target="_blank" rel="noreferrer" className="vf__social-btn" aria-label="Facebook">
            <IconFacebook />
          </a>
          <a href="https://www.instagram.com/visiomate_" target="_blank" rel="noreferrer" className="vf__social-btn" aria-label="Instagram">
            <IconInstagram />
          </a>
          <a href="https://www.youtube.com/@visiomate" target="_blank" rel="noreferrer" className="vf__social-btn" aria-label="YouTube">
            <IconYouTube />
          </a>
        </div>

        <Form className="vf__newsletter" form={form} onFinish={onFinish}>
          <span className="vf__newsletter-label">Stay in the loop</span>
          <div className="vf__newsletter-row">
            <Form.Item
              name="name"
              rules={[{ required: true, message: "Name required" }]}
            >
              <Input placeholder="Name" />
            </Form.Item>
            <Form.Item
              name="email"
              rules={[
                { required: true, message: "Email required" },
                {
                  pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Enter a valid email",
                },
              ]}
            >
              <Input placeholder="Email" type="email" />
            </Form.Item>
            <button
              type="submit"
              className="vf__newsletter-btn"
              disabled={loading || !isVerified}
            >
              {loading ? <Spin size="small" /> : "Subscribe"}
            </button>
          </div>
        </Form>

        <p className="vf__copy">© {new Date().getFullYear()} Visiomate. All rights reserved.</p>
      </div>
    </AntFooter>
  )
}

export default Footer
